import "./App.css";
import mainApi from "../../utils/api.js";
import { preloadScript } from "../../utils/scriptCache.js";
import CurrentUserContext from "../../contexts/CurrentUserContext.jsx";
import { saveCepStorage, getCepStorage } from "../../utils/cepStorage.js";
import { useCepStorage } from "../../hooks/useCepStorage.jsx";
import { useState, useEffect } from "react";
import {
  Navigate,
  useRoutes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import useVisible from "../../hooks/useVisible.jsx";

import Login from "../Login/Login.jsx";
import Register from "../Register/Register.jsx";
import InfoTooltip from "../InfoTooltip/InfoTooltip.jsx";
import SubscriptionCheck from "../SubscriptionCheck/SubscriptionCheck.jsx";
import Header from "../Header/Header.jsx";
import Tags from "../Tags/Tags.jsx";
import ButtonList from "../ButtonList/ButtonList.jsx";
import Footer from "../Footer/Footer.jsx";
import LoadData from "../LoadData/LoadData.jsx";
import Preloader from "../Preloader/Preloader.jsx";
import { getItem, setItem, removeItem } from "../../utils/cepStorage";
import {
  hasCategory,
  isSubscriptionActive,
} from "../../utils/subscription.js";

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTag, setSelectedTag] = useVisible(null);

  const [buttons, setButtons] = useState([]);
  const [isButtonsUpdated, setIsButtonsUpdated] = useState(false);

  const [isLoader, setIsLoader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [isInfoTooltip, setIsInfoTooltip] = useState({
    isOpen: false,
    status: true,
    text: "",
  });

  const [load, setLoad] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [loggedIn, setLoggedIn] = useState(false);
  const [serverError, setServerError] = useState(null);

  function closeInfoTooltip() {
    setIsInfoTooltip({ ...isInfoTooltip, isOpen: false });
  }

  function handleRegister({ name, family, login, email, password }) {
    setIsLoader(true);
    mainApi
      .createUser({ name, family, login, email, password })
      .then((response) => {
        if (response.data.email === email) {
          handleLogin({ email, password });
        }
      })
      .catch((err) => setServerError(err.message))
      .finally(() => setIsLoader(false));
  }

  function handleLogin({ email, password }) {
    setIsLoader(true);
    mainApi
      .login({ email, password })
      .then((jwt) => {
        if (jwt.token) {
          setItem("jwt", jwt.token);
          return mainApi.getUserInfo();
        }
        // Если нет jwt.token, возможно, следует выбросить ошибку или обработать этот случай иначе.
        throw new Error("JWT token is missing.");
      })
      .then((data) => {
        if (data) {
          setLoggedIn(true);
          setCurrentUser(data);
          navigate("/", { replace: true });
        }
      })
      .catch((err) => {
        setServerError(err.message);
        // Если возникла ошибка при входе или получении данных пользователя, может быть полезно очистить jwt из storage
        removeItem("jwt");
      })
      .finally(() => {
        setIsLoader(false);
      });
  }

  function handleSignOut() {
    setCurrentUser({});
    setLoggedIn(false);
    removeItem("jwt");
  }

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      setDataLoaded(true);
    }
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const jwt = getItem("jwt");
    if (jwt) {
      setIsLoader(true);
      mainApi
        .getUserInfo()
        .then((data) => {
          if (data) {
            setLoggedIn(true);
            setCurrentUser(data);
            navigate(path, { replace: true });

          }
        })
        .catch((err) =>
          setIsInfoTooltip({ isOpen: true, status: false, text: err })
        )
        .finally(() => {
          setIsLoader(false);
          setLoad(true);
          setLoading(false);
        });
    } else {
      setLoad(true);
    }
  }, []);

  useEffect(() => {
    const fetchButtons = async () => {
      try {
        setIsLoader(true);

        let fetchedButtons = await mainApi.getButtons();
        fetchedButtons = fetchedButtons.sort((a, b) =>
        a.name.localeCompare(b.name)
        );

        setButtons(fetchedButtons);
        setIsButtonsUpdated(false); // Reset the flag after successfully fetching the buttons
      } catch (err) {
        setIsInfoTooltip({ isOpen: true, status: false, text: err });
      } finally {
        setIsLoader(false);
      }
    };

    if (loggedIn) {
      fetchButtons();
      preloadScript();
    }
  }, [loggedIn, isButtonsUpdated]);

  const routing = useRoutes([
    {
      path: "signup",
      element: loggedIn ? (
        <Navigate to="/" replace />
      ) : (
        <Register handleRegister={handleRegister} serverError={serverError} />
      ),
    },
    {
      path: "signin",
      element: loggedIn ? (
        <Navigate to="/" replace />
      ) : (
        <Login handleLogin={handleLogin} serverError={serverError} />
      ),
    },
    {
      path: "/",
      element: loggedIn ? (
        isSubscriptionActive(currentUser.subscription) &&
        hasCategory(currentUser.subscription, "pp") ? (
          dataLoaded ? (
            <>
              <Header handleSignOut={handleSignOut} />
              <Tags currentTag={selectedTag} onTagChange={setSelectedTag} />
              <ButtonList
              currentUser={currentUser}
                      saveCepStorage={saveCepStorage}
        getCepStorage={getCepStorage}
        useCepStorage={useCepStorage}
        useVisible={useVisible}
                buttons={buttons}
                selectedTag={selectedTag}
              />
              <Footer />
            </>
          ) : (
            process.env.NODE_ENV === "production" && (
              <LoadData
                isOpen={!dataLoaded}
                onDataLoaded={() => setDataLoaded(true)}
              />
            )
          )
        ) : (
          <SubscriptionCheck handleSignOut={handleSignOut} />
        )
      ) : (
        <Navigate to="/signin" />
      ),
    },
  ]);

  return (
    <CurrentUserContext.Provider value={currentUser}>
      <div>
        <InfoTooltip onClose={closeInfoTooltip} isInfoTooltip={isInfoTooltip} />
        {isLoader ? <Preloader isOpen={!dataLoaded} /> : routing}
      </div>
    </CurrentUserContext.Provider>
  );
}
